import { useParams, useOutletContext, Link } from 'react-router-dom';
import { ArrowLeft, Bot, Mail, AlertCircle } from 'lucide-react';
import { Card, CardHeader, Badge, Button } from '@/components/ui';
import { AGENTS, PACK } from '@/lib/pack';
import type { Tenant } from '@/hooks/useTenant';

/**
 * Page placeholder par agent — pour le launch.
 * V1.1 (post-launch) : afficher les derniers livrables réels via paja-agent /api/.
 * Pour l'instant on lie vers un mailto Sebastian pour les premiers clients.
 */
export function AgentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { tenant } = useOutletContext<{ tenant: Tenant }>();
  const meta = id ? AGENTS[id] : undefined;
  const packInfo = PACK[tenant.pack];
  const activeAgents = tenant.active_agents.length > 0 ? tenant.active_agents : packInfo.defaultAgents;
  const isActive = id ? activeAgents.includes(id) : false;

  if (!meta) {
    return (
      <Card>
        <div className="text-center py-8">
          <AlertCircle className="w-10 h-10 text-ducens-warn mx-auto mb-3" />
          <h1 className="text-xl font-semibold mb-2">Agent inconnu</h1>
          <p className="text-ducens-muted text-sm mb-4">
            Aucun agent ne correspond à <code>{id}</code>.
          </p>
          <Button as="link" to="/" variant="primary" size="sm">
            <ArrowLeft className="w-4 h-4" /> Retour au dashboard
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-ducens-muted hover:text-ducens-ink">
        <ArrowLeft className="w-4 h-4" /> Dashboard
      </Link>

      <Card>
        <CardHeader
          title={meta.name}
          description={meta.description}
          action={<Bot className="w-5 h-5 text-ducens-muted" aria-hidden="true" />}
        />

        <div className="flex items-center gap-2 mb-6">
          <span className="text-xs text-ducens-muted">Statut :</span>
          {isActive ? (
            <Badge variant="success">Actif</Badge>
          ) : (
            <Badge variant="neutral">Non activé</Badge>
          )}
        </div>

        <div className="bg-ducens-bg rounded-lg p-4 mb-6">
          <h3 className="text-sm font-semibold mb-1">Pendant le launch</h3>
          <p className="text-sm text-ducens-muted">
            Sebastian te livre les outputs en direct par email pour les premiers cycles. Le panel
            d'historique en self-service arrive en V1.1 — d'ici-là, demande directement tes derniers
            livrables et il les renvoie.
          </p>
        </div>

        <Button
          as="link"
          to={`mailto:sebastian@ducens.io?subject=${encodeURIComponent(
            `[${meta.name}] derniers livrables — ${tenant.company_name}`
          )}`}
          external
          variant="primary"
          size="md"
        >
          <Mail className="w-4 h-4" />
          Voir mes derniers livrables
        </Button>
      </Card>
    </div>
  );
}
